import React, { useCallback, useEffect, useRef, useState } from "react";
import "./Battleship.css";

const BOARD_SIZE = 10;
const SHIPS = [
  { name: "Carrier", length: 5, id: "C" },
  { name: "Battleship", length: 4, id: "B" },
  { name: "Cruiser", length: 3, id: "R" },
  { name: "Submarine", length: 3, id: "S" },
  { name: "Destroyer", length: 2, id: "D" },
];

const coordsToKey = (r, c) => `${r},${c}`;
const rand = (n) => Math.floor(Math.random() * n);

function placeShipsRandomly() {
  const board = Array.from({ length: BOARD_SIZE }, () =>
    Array.from({ length: BOARD_SIZE }, () => ({ shipId: null, hit: false }))
  );
  const shipsPlaced = SHIPS.map((ship) => {
    let placed = false;
    let placements = [];
    while (!placed) {
      const horiz = Math.random() < 0.5;
      const r = rand(BOARD_SIZE);
      const c = rand(BOARD_SIZE);
      const cells = [];
      for (let i = 0; i < ship.length; i++) {
        const rr = r + (horiz ? 0 : i);
        const cc = c + (horiz ? i : 0);
        if (rr >= BOARD_SIZE || cc >= BOARD_SIZE) {
          cells.length = 0;
          break;
        }
        cells.push([rr, cc]);
      }
      if (cells.length !== ship.length) continue;
      const overlap = cells.some(([rr, cc]) => board[rr][cc].shipId !== null);
      if (!overlap) {
        cells.forEach(([rr, cc]) => (board[rr][cc].shipId = ship.id));
        placed = true;
        placements = cells;
      }
    }
    return { ...ship, cells: placements, hits: 0, sunk: false };
  });
  return { board, ships: shipsPlaced };
}

function checkSunk(ship, board) {
  return ship.cells.every(([r, c]) => board[r][c].hit);
}
function remainingShips(ships) {
  console.log(ships.filter((s) => !s.sunk).length);
  return ships.filter((s) => !s.sunk).length;
}

export default function BattleshipGame() {
  const [playerBoard, setPlayerBoard] = useState(() =>
    Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => ({ shipId: null, hit: false }))
    )
  );
  const [playerShips, setPlayerShips] = useState([]);
  const [placingIndex, setPlacingIndex] = useState(0);
  const [isPlacing, setIsPlacing] = useState(true);
  const [horizontal, setHorizontal] = useState(true);
  const [hoverCells, setHoverCells] = useState([]);
  const [cpuBoard, setCpuBoard] = useState(() => placeShipsRandomly());
  const [cpuReveal, setCpuReveal] = useState(() =>
    Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => ({ fired: false, hit: false }))
    )
  );
  const [playerReveal, setPlayerReveal] = useState(() =>
    Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => ({ fired: false, hit: false }))
    )
  );

  const [playerTurn, setPlayerTurn] = useState(false);
  const [message, setMessage] = useState("Place your ships to begin. You can press 'R' to rotate your ships.");
  const [showEnemy, setShowEnemy] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  // --- Simplified control refs and timeout cleanup ---
  const gameOverRef = useRef(false);
  const cpuTimeoutRef = useRef(null);
  const playerRevealRef = useRef(playerReveal);
  useEffect(() => { playerRevealRef.current = playerReveal; }, [playerReveal]);
  useEffect(() => { gameOverRef.current = gameOver; }, [gameOver]);

  const cpuTargetsRef = useRef([]);

  const stopCpu = () => {
    if (cpuTimeoutRef.current) {
      clearTimeout(cpuTimeoutRef.current);
      cpuTimeoutRef.current = null;
    }
  };

  useEffect(() => {
    const handleKey = (e) => {
      if (isPlacing && e.key === "r") setHorizontal((h) => !h);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isPlacing]);

  // Recalculate hover cells when rotation changes
  useEffect(() => {
    if (isPlacing && hoverCells.length) {
      const [r, c] = hoverCells[0]; // use the first hovered cell as anchor
      handleCellHover(r, c);
    }
  }, [horizontal]);

  const handleCellHover = (r, c) => {
    if (!isPlacing) return;
    const ship = SHIPS[placingIndex];
    const cells = [];
    for (let i = 0; i < ship.length; i++) {
      const rr = r + (horizontal ? 0 : i);
      const cc = c + (horizontal ? i : 0);
      if (rr < BOARD_SIZE && cc < BOARD_SIZE) cells.push([rr, cc]);
    }
    setHoverCells(cells);
  };

  const placeShipAt = (r, c) => {
    if (!isPlacing) return;
    const ship = SHIPS[placingIndex];
    const cells = [];
    for (let i = 0; i < ship.length; i++) {
      const rr = r + (horizontal ? 0 : i);
      const cc = c + (horizontal ? i : 0);
      if (rr >= BOARD_SIZE || cc >= BOARD_SIZE) return;
      cells.push([rr, cc]);
    }
    // check overlap
    const overlap = cells.some(([rr, cc]) => playerBoard[rr][cc].shipId);
    if (overlap) return;

    const newBoard = playerBoard.map((row) => row.map((cell) => ({ ...cell })));
    cells.forEach(([rr, cc]) => (newBoard[rr][cc].shipId = ship.id));
    setPlayerBoard(newBoard);

    const newShips = [...playerShips, { ...ship, cells, hits: 0, sunk: false }];
    setPlayerShips(newShips);

    if (placingIndex + 1 >= SHIPS.length) {
      setIsPlacing(false);
      setMessage("All ships placed! Your turn to attack.");
      setPlayerTurn(true);
    } else {
      setPlacingIndex((i) => i + 1);
    }
  };

  const resetGame = () => {
    stopCpu();
    cpuTargetsRef.current = [];
    setGameOver(false);
    gameOverRef.current = false;

    // Reset boards
    const emptyBoard = Array.from({ length: BOARD_SIZE }, () =>
      Array.from({ length: BOARD_SIZE }, () => ({ shipId: null, hit: false }))
    );
    setPlayerBoard(emptyBoard);
    setCpuBoard(placeShipsRandomly());
    setCpuReveal(
      Array.from({ length: BOARD_SIZE }, () =>
        Array.from({ length: BOARD_SIZE }, () => ({ fired: false, hit: false }))
      )
    );
    setPlayerReveal(
      Array.from({ length: BOARD_SIZE }, () =>
        Array.from({ length: BOARD_SIZE }, () => ({ fired: false, hit: false }))
      )
    );
    setPlayerShips([]);
    setPlacingIndex(0);
    setIsPlacing(true);
    setMessage("Place your ships to begin. You can press 'R' to rotate your ships.");
    setPlayerTurn(false);
  };

  const playerFire = (r, c) => {
    if (!playerTurn || gameOver || isPlacing) return;
    const gridCopy = cpuReveal.map((row) => row.map((cell) => ({ ...cell })));
    if (gridCopy[r][c].fired) return;

    gridCopy[r][c].fired = true;
    const cell = cpuBoard.board[r][c];
    gridCopy[r][c].hit = !!cell.shipId;
    setCpuReveal(gridCopy);

    if (cell.shipId) {
      const shipIndex = cpuBoard.ships.findIndex((s) => s.id === cell.shipId);
      const updatedShips = [...cpuBoard.ships];
      updatedShips[shipIndex].hits++;

      if (checkSunk(updatedShips[shipIndex], gridCopy)) {
        updatedShips[shipIndex].sunk = true;
        setMessage(`You sunk the enemy's ${updatedShips[shipIndex].name}!`);
      } else {
        setMessage("Hit! You get another shot!");
      }

      if (remainingShips(updatedShips) === 0) {
        setMessage("You win!");
        setGameOver(true);
        gameOverRef.current = true;
        stopCpu();
        return;
      }

      setCpuBoard({ ...cpuBoard, ships: updatedShips });
    } else {
      setMessage("Miss!");
      setPlayerTurn(false);

      if (!gameOverRef.current) {
        cpuTimeoutRef.current = setTimeout(() => {
          if (!gameOverRef.current) cpuTurn();
        }, 600);
      }
    }
  };


  const cpuTurn = () => {
    // Hard stop if game ended
    if (gameOverRef.current) {
      stopCpu();
      return;
    }

    let shot = null;
    const targets = cpuTargetsRef.current;

    // Weighted target selection
    if (targets.length && Math.random() < 0.8) {
      while (targets.length) {
        const [tr, tc] = targets.shift();
        if (
          tr >= 0 && tr < BOARD_SIZE &&
          tc >= 0 && tc < BOARD_SIZE &&
          !playerRevealRef.current[tr][tc].fired
        ) {
          shot = [tr, tc];
          break;
        }
      }
    }

    // Random fallback
    if (!shot) {
      const candidates = [];
      for (let r = 0; r < BOARD_SIZE; r++) {
        for (let c = 0; c < BOARD_SIZE; c++) {
          if (!playerRevealRef.current[r][c].fired) candidates.push([r, c]);
        }
      }
      if (candidates.length === 0) return;
      shot = candidates[rand(candidates.length)];
    }

    const [r, c] = shot;
    setPlayerReveal((prev) => {
    const copy = prev.map((row) => row.map((cell) => ({ ...cell })));
    copy[r][c].fired = true;
    copy[r][c].hit = !!playerBoard[r][c].shipId;
    return copy;
  });

  const hit = !!playerBoard[r][c].shipId;
  if (hit) {
    const shipId = playerBoard[r][c].shipId;

    // update ships outside of setPlayerReveal
    setPlayerShips((prevShips) => {
      const newShips = prevShips.map((s) => ({ ...s }));
      const shipIdx = newShips.findIndex((s) => s.id === shipId);
      if (shipIdx >= 0) {
        newShips[shipIdx].hits++;

        // check if ship is sunk
        const ship = newShips[shipIdx];
        const isSunk = ship.cells.every(([rr, cc]) => playerRevealRef.current[rr][cc].hit || (rr === r && cc === c));
        if (isSunk && !ship.sunk) {
          ship.sunk = true;
          setMessage(`CPU sunk your ${ship.name}!`);
        }
      }
      return newShips;
    });
  }

  // Queue adjacent cells for next move if hit
  if (hit) {
    const dirs = [[0,1],[1,0],[0,-1],[-1,0]];
    for (const [dr, dc] of dirs) {
      const nr = r + dr, nc = c + dc;
      if (
        nr >= 0 && nr < BOARD_SIZE &&
        nc >= 0 && nc < BOARD_SIZE &&
        !playerRevealRef.current[nr][nc].fired
      ) cpuTargetsRef.current.push([nr, nc]);
    }

    cpuTimeoutRef.current = setTimeout(() => {
      if (!gameOverRef.current) cpuTurn();
    }, 600);
    return;
  }

  // if miss, switch turn
  setMessage("CPU missed. Your turn!");
  setPlayerTurn(true);

  // hard stop: check if total hits equal total ship tiles
  const totalShipTiles = playerShips.reduce((sum, s) => sum + s.length, 0);
  let hitCount = 0;
  for (let rr = 0; rr < BOARD_SIZE; rr++) {
    for (let cc = 0; cc < BOARD_SIZE; cc++) {
      if (playerRevealRef.current[rr][cc].hit) hitCount++;
    }
  }
  if (hitCount >= totalShipTiles) {
    setMessage("CPU wins!");
    setGameOver(true);
    gameOverRef.current = true;
    stopCpu();
    return;
  }
};

  const renderPlayerCell = (r, c) => {
    const cell = playerBoard[r][c];
    const reveal = playerReveal[r][c];
    const isHover = hoverCells.some(([rr, cc]) => rr === r && cc === c);
    const classes = ["cell"];
    if (cell.shipId) classes.push("ship-cell");
    if (reveal.fired) classes.push(reveal.hit ? "hit" : "miss");
    if (isPlacing && isHover) classes.push("hover-cell", horizontal ? "horizontal" : "vertical");
    return (
      <div
        key={coordsToKey(r, c)}
        className={classes.join(" ")}
        onMouseEnter={() => handleCellHover(r, c)}
        onClick={() => placeShipAt(r, c)}
      />
    );
  };

  const renderCpuCell = (r, c) => {
    const reveal = cpuReveal[r]?.[c];
    const cell = cpuBoard.board[r][c];
    if (!reveal) return null;
    const classes = ["cell"];
    if (reveal.fired) classes.push(reveal.hit ? "hit" : "miss");
    else if (showEnemy && cell.shipId) classes.push("debug-ship");
    return (
      <div
        key={coordsToKey(r, c)}
        className={classes.join(" ")}
        onClick={() => playerFire(r, c)}
      />
    );
  };

  /* Add for debugging
  {!isPlacing && (
        <div className="controls">
          <button onClick={() => setShowEnemy((s) => !s)}>
            {showEnemy ? "Hide Enemy Ships" : "Show Enemy Ships"}
          </button>
        </div>
      ) NOTE THIS IS A DEBUG BUTTON THAT DISPLAYS ENEMIES}*/
  return (
    <div className="battleship-wrap">
      <h2>Battleship</h2>
      {isPlacing && (
        <div className="placement-panel">
          <p>
            Placing: <strong>{SHIPS[placingIndex].name}</strong> (
            {SHIPS[placingIndex].length})
          </p>
          <button onClick={() => setHorizontal((h) => !h)} style={{color: "white"}}>
            Rotate ({horizontal ? "Horizontal" : "Vertical"})
          </button>
        </div>
      ) /*}{ (
        <div className="controls">
          <button onClick={() => setShowEnemy((s) => !s)}>
            {showEnemy ? "Hide" : "Show"} Enemy Ships
          </button>
        </div>
      )*/}

      <div className="boards">
        <div>
          <h3>Your Fleet</h3>
          <div
            className="board"
            style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)` }}
          >
            {Array.from({ length: BOARD_SIZE }).map((_, r) =>
              Array.from({ length: BOARD_SIZE }).map((_, c) =>
                renderPlayerCell(r, c)
              )
            )}
          </div>
        </div>

        <div>
          <h3>Enemy Waters</h3>
          <div
            className="board"
            style={{ gridTemplateColumns: `repeat(${BOARD_SIZE}, 1fr)` }}
          >
            {Array.from({ length: BOARD_SIZE }).map((_, r) =>
              Array.from({ length: BOARD_SIZE }).map((_, c) =>
                renderCpuCell(r, c)
              )
            )}
          </div>
        </div>
      </div>

      <div className="status">{message}</div>
      {gameOver && (
        <div className="game-over-modal">
          <div className="game-over-content">

            <button className="game-over-close" onClick={resetGame}>✕</button>

            <h2 className="game-over-color">{message}</h2>
            <button className="game-over-play" onClick={resetGame}>Play Again</button>
          </div>
        </div>
      )}
    </div>
  );
}
